// Basic touch-friendly cropper. Shows a just-captured JPEG with a free-form,
// draggable/resizable rectangle; on confirm it re-encodes the selected region to
// a new JPEG Blob (camera.cropJpeg) and hands it back. Cropping to just the
// artwork improves reverse-image lookups and the kept fallback image (DESIGN §7).
// No new dependencies — Pointer Events give clean iPad-Safari drag/resize.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cropJpeg, rotateJpeg, type CropRect } from "../camera";

export interface CropModalProps {
  /** The captured JPEG to crop. */
  source: Blob;
  /**
   * User confirmed: receives an image/jpeg Blob of the selected region. If they
   * chose "Use full photo" it's the (un-cropped) working image — which is the
   * original `source`, or a rotated re-encode of it if they used Rotate. Either
   * way it's always image/jpeg, ready for the presigned PUT.
   */
  onConfirm: (result: Blob) => void;
  /** User backed out of cropping (does not discard the capture). */
  onCancel: () => void;
  title?: string;
  confirmLabel?: string;
  /** Disable controls while the parent is uploading. */
  busy?: boolean;
  /** Error from the parent's submit (e.g. upload failed), shown in-modal. */
  error?: string | null;
}

type Corner = "nw" | "ne" | "sw" | "se";
type DragMode = "move" | Corner;

interface DragState {
  mode: DragMode;
  startX: number;
  startY: number;
  startRect: CropRect;
}

const MIN = 40; // minimum crop size in displayed px

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function CropModal({
  source,
  onConfirm,
  onCancel,
  title = "Crop the photo",
  confirmLabel = "Use crop",
  busy = false,
  error = null,
}: CropModalProps) {
  const [url, setUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const naturalRef = useRef<{ w: number; h: number } | null>(null);
  // Crop rect in DISPLAYED pixels, relative to the rendered image's top-left.
  const [rect, setRect] = useState<CropRect | null>(null);
  const drag = useRef<DragState | null>(null);
  const [working, setWorking] = useState(false);
  // The image we actually crop. Starts as `source`; the Rotate button replaces it
  // with a re-encoded, rotated JPEG. Resetting it when the `source` prop changes
  // keeps reuse (e.g. RecaptureModal capturing a fresh frame) correct.
  const [workingSource, setWorkingSource] = useState<Blob>(source);
  const [rotating, setRotating] = useState(false);

  // A brand-new source prop means a fresh image: adopt it and drop any prior crop
  // rect so measure() re-seeds the default box against the new dimensions.
  useEffect(() => {
    setWorkingSource(source);
    setRect(null);
  }, [source]);

  // Object URL for the working (possibly rotated) blob.
  useEffect(() => {
    const u = URL.createObjectURL(workingSource);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [workingSource]);

  // Rotate the working image 90° clockwise. Re-encodes to a new JPEG and clears
  // the crop rect so the default box re-seeds against the swapped dimensions when
  // the rotated image loads (measure()).
  const rotate = useCallback(async () => {
    if (busy || working || rotating) return;
    setRotating(true);
    try {
      const rotated = await rotateJpeg(workingSource, 90);
      naturalRef.current = null;
      setRect(null);
      setWorkingSource(rotated);
    } finally {
      setRotating(false);
    }
  }, [busy, working, rotating, workingSource]);

  // Measure the rendered image and (re)seed/clamp the crop rect. Runs on load
  // and on viewport changes (e.g. iPad rotation).
  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.clientWidth || !img.clientHeight) return;
    const w = img.clientWidth;
    const h = img.clientHeight;
    naturalRef.current = { w: img.naturalWidth, h: img.naturalHeight };
    setImgSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    setRect((prev) => {
      if (!prev) {
        return { x: w * 0.08, y: h * 0.08, w: w * 0.84, h: h * 0.84 };
      }
      // Re-clamp an existing rect into the new bounds.
      const rw = Math.min(prev.w, w);
      const rh = Math.min(prev.h, h);
      return {
        w: rw,
        h: rh,
        x: clamp(prev.x, 0, w - rw),
        y: clamp(prev.y, 0, h - rh),
      };
    });
  }, []);

  useLayoutEffect(() => {
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [measure]);

  const onPointerDown = (mode: DragMode) => (e: React.PointerEvent) => {
    if (busy || working || !rect) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    drag.current = { mode, startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const size = imgSize;
    if (!d || !size) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const s = d.startRect;
    let next: CropRect;
    if (d.mode === "move") {
      next = {
        ...s,
        x: clamp(s.x + dx, 0, size.w - s.w),
        y: clamp(s.y + dy, 0, size.h - s.h),
      };
    } else {
      let { x, y, w, h } = s;
      if (d.mode === "nw" || d.mode === "sw") {
        const nx = clamp(s.x + dx, 0, s.x + s.w - MIN);
        x = nx;
        w = s.x + s.w - nx;
      } else {
        w = clamp(s.w + dx, MIN, size.w - s.x);
      }
      if (d.mode === "nw" || d.mode === "ne") {
        const ny = clamp(s.y + dy, 0, s.y + s.h - MIN);
        y = ny;
        h = s.y + s.h - ny;
      } else {
        h = clamp(s.h + dy, MIN, size.h - s.y);
      }
      next = { x, y, w, h };
    }
    setRect(next);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (drag.current) {
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* pointer may already be released */
      }
      drag.current = null;
    }
  };

  const useCrop = async () => {
    const nat = naturalRef.current;
    if (!rect || !imgSize || !nat) {
      onConfirm(workingSource);
      return;
    }
    setWorking(true);
    const scaleX = nat.w / imgSize.w;
    const scaleY = nat.h / imgSize.h;
    const naturalRect: CropRect = {
      x: rect.x * scaleX,
      y: rect.y * scaleY,
      w: rect.w * scaleX,
      h: rect.h * scaleY,
    };
    const cropped = await cropJpeg(workingSource, naturalRect);
    setWorking(false);
    onConfirm(cropped);
  };

  const disabled = busy || working || rotating;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal">
        <header className="modal__header">
          <h2>{title}</h2>
          <div className="modal__header-actions" style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void rotate()}
              disabled={disabled}
              title="Rotate the photo 90°"
            >
              {rotating ? "Rotating…" : "Rotate 90°"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={disabled}>
              Cancel
            </button>
          </div>
        </header>

        <p className="modal__note">
          Drag the box to frame just the artwork (skip the page edges, captions and glare).
          A tight crop gives better online matches and a cleaner fallback image.
        </p>

        <div className="crop-stage">
          <div className="crop-wrap">
            {url && (
              <img
                ref={imgRef}
                src={url}
                className="crop-img"
                alt="Captured frame to crop"
                draggable={false}
                onLoad={measure}
              />
            )}
            {rect && (
              <div
                className="crop-rect"
                style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                onPointerDown={onPointerDown("move")}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                {(["nw", "ne", "sw", "se"] as Corner[]).map((c) => (
                  <span
                    key={c}
                    className={`crop-handle crop-handle--${c}`}
                    onPointerDown={onPointerDown(c)}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}

        <footer className="modal__footer modal__footer--split">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => onConfirm(workingSource)}
            disabled={disabled}
          >
            Use full photo
          </button>
          <button
            type="button"
            className="btn btn--primary btn--large"
            onClick={() => void useCrop()}
            disabled={disabled}
          >
            {disabled ? "Working…" : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
